import { uniqueId } from 'lodash';
import { GiftedChat } from 'react-native-gifted-chat';
import apis from '@api/api';
import { SET_RECOMMENDED_INTEGRATIONS, COMPLETE_ONBOARDING } from '@reduxLocal/actions/types';

export interface IntegrationHandlersConfig {
  dispatch: any;
  globalUser: any;
  userData: any;
  queryClient: any;
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setOnboardingStage?: (stage: 'profile' | 'context' | 'models' | null) => void;
  setSavedProfileData?: (data: Record<string, any>) => void;
}

/**
 * Fetch integration suggestions after onboarding
 */
export const createFetchIntegrationSuggestions = (config: IntegrationHandlersConfig) => {
  const { dispatch, globalUser, userData, queryClient, setChatMessages, setOnboardingStage, setSavedProfileData } = config;

  return async () => {
    try {
      console.log('🔍 [EasyModeScreen] Fetching integration suggestions...');
      console.log('📋 [EasyModeScreen] Current user context:', {
        userId: globalUser?._id || globalUser?.id,
        companyId: userData?.companyId || userData?.company?._id,
      });

      const response = await apis.getIntegrationSuggestions();
      console.log('📥 [EasyModeScreen] Integration suggestions API response:', {
        success: response?.success,
        hasData: !!response?.data,
        suggestionsCount: response?.data?.suggestions?.length || 0,
        response: response,
      });

      if (response?.success && response?.data?.suggestions?.length > 0) {
        const suggestions = response.data.suggestions;
        console.log('✅ [EasyModeScreen] Received integration suggestions:', suggestions.length);

        const fullIntegrationData = queryClient.getQueryData(['integrationData']) as any[];
        console.log('📚 [EasyModeScreen] Full integration data from cache:', {
          available: !!fullIntegrationData,
          count: fullIntegrationData?.length || 0,
        });

        const enrichedSuggestions = suggestions.map((suggestion: any) => {
          const fullIntegration = fullIntegrationData?.find((intg) => {
            const intgId = intg._id || intg.id;
            const intgMeta = (intg.meta || '').toLowerCase();
            const intgTitle = (intg.title || '').toLowerCase();
            const suggestionId = suggestion._id;
            const suggestionMeta = (suggestion.meta || '').toLowerCase();
            const suggestionTitle = (suggestion.title || '').toLowerCase();

            return (
              (suggestionId && intgId && String(intgId) === String(suggestionId)) ||
              (suggestionMeta && intgMeta && intgMeta === suggestionMeta) ||
              (suggestionTitle && intgTitle && intgTitle === suggestionTitle)
            );
          });

          if (fullIntegration) {
            return {
              ...fullIntegration,
              relevance_score: suggestion.relevance_score,
              relevance_reasoning: suggestion.relevance_reasoning,
              key_match_factors: suggestion.key_match_factors || [],
            };
          } else {
            console.warn(`⚠️ [EasyModeScreen] Could not find full integration data for suggestion:`, {
              title: suggestion.title,
              meta: suggestion.meta,
              _id: suggestion._id,
            });
            return suggestion;
          }
        });

        const recommendedIds = suggestions
          .map((suggestion: any) => suggestion.meta || suggestion._id || suggestion.title?.toLowerCase())
          .filter(Boolean);

        console.log('🏷️ [EasyModeScreen] Storing recommended integration IDs in Redux:', recommendedIds);
        dispatch({
          type: SET_RECOMMENDED_INTEGRATIONS,
          payload: recommendedIds,
        });

        const suggestionMessageId = uniqueId();
        setChatMessages((prevMessages) =>
          GiftedChat.append(prevMessages, [
            {
              _id: suggestionMessageId,
              text:
                response.data.reasoning ||
                'Based on your profile, here are some integrations that might be helpful for your business:',
              createdAt: new Date(),
              system: true,
              suggestedIntegrations: enrichedSuggestions,
              received: true,
            },
          ])
        );

        console.log('💬 [EasyModeScreen] Integration suggestions message injected into chat');
        
        // Complete onboarding AFTER integration suggestions are shown
        setTimeout(() => {
          dispatch({ type: COMPLETE_ONBOARDING });
          if (setOnboardingStage) setOnboardingStage(null);
          if (setSavedProfileData) setSavedProfileData({});
          console.log('✅ [EasyModeScreen] Onboarding marked as complete (after integration suggestions)');
        }, 1000);
      } else {
        console.warn('⚠️ [EasyModeScreen] No integration suggestions received. Response:', JSON.stringify(response, null, 2));
        
        // Complete onboarding even if no integrations found
        setTimeout(() => {
          dispatch({ type: COMPLETE_ONBOARDING });
          if (setOnboardingStage) setOnboardingStage(null);
          if (setSavedProfileData) setSavedProfileData({});
          console.log('✅ [EasyModeScreen] Onboarding marked as complete (no integration suggestions)');
        }, 500);
      }
    } catch (error: any) {
      console.error('❌ [EasyModeScreen] Error fetching integration suggestions:', error);
      console.error('❌ [EasyModeScreen] Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        stack: error?.stack,
      });
      
      // Complete onboarding even on error
      setTimeout(() => {
        dispatch({ type: COMPLETE_ONBOARDING });
        if (setOnboardingStage) setOnboardingStage(null);
        if (setSavedProfileData) setSavedProfileData({});
        console.log('✅ [EasyModeScreen] Onboarding marked as complete (error fetching integrations)');
      }, 500);
    }
  };
};

